/**
 * ThinkingIndicator — replaces the static "Thinking…" pill while the AI
 * agent is processing a turn.
 *
 * Why:
 *   The previous pill ("Thinking…" / "Pulling data…") felt robotic. Real
 *   investigations involve several distinct phases — reading the question,
 *   pulling notes, querying collections, comparing totals, drafting an
 *   answer — and a single static label hides all of that.
 *
 * What this does:
 *   1. Picks a "track" of phrases that matches what the user just asked.
 *      Tracks are derived from light keyword sniffing on the message:
 *        • notes / why / explain / mismatch       → investigation track
 *        • payment file / fund pull / approved    → payment-file track
 *        • report / csv / export                  → report track
 *        • where / how / take me / show me        → navigation track
 *        • default → generic data track
 *   2. Cross-fades through phrases every ~1.9s and gently slows toward the
 *      end (the last phrase, "Nearly there…", lingers longer so it never
 *      cuts off mid-rotate).
 *   3. The phrase text wears a slow-moving aurora gradient
 *      (violet → blue → cyan with BillPortal red as the accent) for a
 *      living, "thinking" feel — but the background stays neutral so it
 *      reads cleanly on the chat surface.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

const TRACK_PHRASES = {
  investigation: [
    'Reading your question…',
    'Checking recent notes…',
    'Cross-referencing bill records…',
    'Pulling the numbers…',
    'Comparing totals…',
    'Reviewing what came back…',
    'Drafting an explanation…',
    'Nearly there…',
  ],
  payment_file: [
    'Looking into this payment file…',
    'Pulling Funds Pulled lines…',
    'Reviewing Approved Paid bills…',
    'Reading ops notes for context…',
    'Reconciling the two views…',
    'Double-checking the numbers…',
    'Composing a clear summary…',
    'Nearly there…',
  ],
  report: [
    'Got it — preparing your report…',
    'Pulling the rows we need…',
    'Spinning up the report worker…',
    'Reviewing what came back…',
    'Tightening the filter if needed…',
    'Packaging the data…',
    'Almost ready to download…',
  ],
  navigation: [
    'Searching the platform map…',
    'Finding the right page…',
    'Composing a quick path…',
    'Nearly there…',
  ],
  outreach: [
    'Reading your request…',
    'Scanning overdue bills…',
    'Reviewing vendor accounts…',
    'Looking up vendor contacts…',
    'Batching account numbers per vendor…',
    'Drafting personalised messages…',
    'Lining them up for your approval…',
    'Nearly there…',
  ],
  contract: [
    'Reading your question…',
    'Pulling up the contract…',
    'Checking contract terms & dates…',
    'Reviewing pricing and hedge details…',
    'Cross-referencing vendor & linked sites…',
    'Comparing against actual usage…',
    'Drafting a clear answer…',
    'Nearly there…',
  ],
  vendor_enquiry: [
    'Reading your request…',
    'Reviewing the vendor details…',
    'Gathering setup requirements…',
    'Looking up the vendor contact…',
    'Drafting your enquiry email…',
    'Adding the right account context…',
    'Lining it up for your approval…',
    'Nearly there…',
  ],
  data: [
    'Looking into your data…',
    'Pulling a quick read…',
    'Compiling totals…',
    'Reviewing what came back…',
    'Drafting the answer…',
    'Nearly there…',
  ],
  thinking: [
    'Thinking…',
    'Looking into it…',
    'Compiling…',
    'Nearly there…',
  ],
};

const KEYWORD_TRACKS = [
  // Vendor setup / enquiry emails take top priority — the user is asking to
  // draft an email to enquire about vendor setup or general vendor enquiries.
  // This overlaps with "draft email" (outreach) and "vendor"/"account"
  // (data), so it must be checked first.
  { track: 'vendor_enquiry', kws: [
    'vendor setup', 'vendor set up', 'set up vendor', 'set up a vendor',
    'setup vendor', 'setup a vendor', 'vendor enquiry', 'vendor enquiries',
    'vendor inquiry', 'vendor inquiries', 'enquire for vendor', 'enquiry for vendor',
    'inquire for vendor', 'inquiry for vendor', 'enquire about vendor', 'enquire vendor',
    'vendor onboarding', 'onboard vendor', 'onboard a vendor', 'new vendor account',
    'vendor account setup', 'set up the vendor', 'vendor registration',
    'email to enquire', 'email to inquire', 'enquiry email', 'inquiry email',
  ] },
  // Contract questions — any question related to a procurement/supply contract.
  // Checked before investigation/payment_file/data so "why is my contract…"
  // and "show my contracts" both land here.
  { track: 'contract', kws: [
    'contract', 'contracts', 'supply contract', 'utility contract',
    'block & index', 'block and index', 'index contract', 'fixed contract',
    'block contract', 'contract rate', 'contract term', 'contract expiry',
    'expiring contract', 'contract pricing', 'hedge', 'procurement contract',
  ] },
  // Outreach takes priority — it overlaps with "email" but the user is
  // clearly asking for vendor contact.
  { track: 'outreach', kws: [
    'email vendor', 'email all vendor', 'email the vendor', 'email overdue',
    'contact vendor', 'contact the vendor', 'reach out to vendor',
    'draft email', 'draft an email', 'draft emails', 'send vendor',
    'request extension', 'request an extension', 'vendor extension',
    'follow up with vendor', 'follow up with vendors', 'vendor outreach',
  ] },
  // Investigation comes next — it overlaps with payment_file but the
  // user has clearly asked a "why?" question.
  { track: 'investigation', kws: ['why', 'mismatch', "doesn't match", 'dont match', "don't match",
    'discrepancy', 'explain', 'investigate', 'off by', 'short by', 'issue', 'wrong', 'differ'] },
  { track: 'payment_file', kws: ['payment file', 'pf ', 'funds pulled', 'fund pulled', 'fund pull',
    'approved paid', 'pulled vs paid', 'pulled paid', 'payment_file', 'pf-'] },
  { track: 'report', kws: ['report', 'csv', 'export', 'download', 'spreadsheet'] },
  { track: 'navigation', kws: ['where is', 'where can i', 'how do i', 'take me to', 'open the',
    'show me the page', 'navigate'] },
  { track: 'data', kws: ['show', 'list', 'top ', 'how many', 'how much', 'what is', 'whats',
    "what's", 'bills', 'overdue', 'paid', 'unpaid', 'refund', 'variance', 'transaction'] },
];

const pickTrack = (lastUserMessage = '') => {
  const t = (lastUserMessage || '').toLowerCase();
  for (const row of KEYWORD_TRACKS) {
    if (row.kws.some((k) => t.includes(k))) return row.track;
  }
  return 'data';
};

const ROTATE_MS = 1900;

// Reassurance phrases — used to keep the user engaged on long-running turns
// (>~12s) so the indicator doesn't freeze on "Nearly there…".
const SUSTAIN_PHRASES = [
  'Still working on it…',
  'Reviewing the output…',
  'Looks light — broadening the search…',
  'Re-checking the numbers…',
  'Polishing the answer…',
  'Crunching the last bits…',
  'Almost ready…',
  'Wrapping up…',
];

export const ThinkingIndicator = ({ lastUserMessage = '', tool = null }) => {
  const phrases = useMemo(() => {
    // If the backend told us a specific tool is running, override the
    // first phrase so it matches reality.
    const baseTrack = pickTrack(lastUserMessage);
    const base = TRACK_PHRASES[baseTrack] || TRACK_PHRASES.data;
    if (tool === 'generate_csv_report') return TRACK_PHRASES.report;
    if (tool === 'describe_page' || tool === 'build_deep_link') return TRACK_PHRASES.navigation;
    if (tool === 'find_overdue_bills_by_vendor' || tool === 'draft_email' || tool === 'approve_emails_batch') {
      // Keep the more specific vendor-enquiry/contract tracks if the message
      // clearly asked for those; otherwise fall back to generic outreach.
      if (baseTrack === 'vendor_enquiry' || baseTrack === 'contract') return base;
      return TRACK_PHRASES.outreach;
    }
    return base;
  }, [lastUserMessage, tool]);

  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [phrases]);

  useEffect(() => {
    const id = setTimeout(() => setIdx((i) => i + 1), ROTATE_MS);
    return () => clearTimeout(id);
  }, [idx]);

  // Pick the phrase: walk through the track, then cycle through SUSTAIN_PHRASES
  // forever so the indicator never freezes during long turns.
  let phrase;
  if (idx < phrases.length) {
    phrase = phrases[idx];
  } else {
    phrase = SUSTAIN_PHRASES[(idx - phrases.length) % SUSTAIN_PHRASES.length];
  }

  return (
    <div
      data-testid="ai-agent-thinking-indicator"
      className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1.5 text-xs"
    >
      <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
      <span
        key={phrase /* re-mount each phrase so the fade-in re-runs */}
        data-testid="ai-agent-thinking-phrase"
        className="ai-thinking-text"
      >
        {phrase}
      </span>
    </div>
  );
};

export default ThinkingIndicator;
